import { useEffect, useRef, useState } from 'react';
import { Message } from '@/services/chatService';
import { Loader2 } from 'lucide-react';
import { MessageActions } from './MessageActions';
import { SourcesCard } from './SourcesCard';
import { FilePreview } from './FilePreview';
import { DocumentDownload } from './DocumentDownload';
import { TextSelectionTooltip } from './TextSelectionTooltip';
import { LegalMarkdown } from './LegalMarkdown';
import { GammaAutoSuggest, detectGammaIntent } from './GammaAutoSuggest';
import { LegalSearchResults } from './LegalSearchResults';
import badgePN from '@/assets/badge-policia-nacional.png';
import badgeGC from '@/assets/badge-guardia-civil.png';

// Detects if the message content is only auto-generated file text (no real user question)
function isFileOnlyMessage(message: Message): boolean {
  if (!message || !message.content || typeof message.content !== 'string') return false;
  if (!message.metadata?.files?.length) return false;
  const content = message.content.trim();
  // Pattern: "N documento(s) adjunto(s)"
  if (/^\d+ documentos? adjuntos?$/i.test(content)) return true;
  // Pattern: starts with 📄
  if (/^📄/.test(content)) return true;
  // Pattern: extracted text with "Documento N:" markers
  if (/Documento \d+:/.test(content)) return true;
  // Pattern: content is very long compared to a normal question (likely extracted text)
  if (content.length > 500 && message.metadata.files.length > 0) return true;
  return false;
}

// Gets only the user-typed text, removing any extracted file content
function getUserTextContent(message: Message): string {
  if (!message || !message.content || typeof message.content !== 'string') return '';
  const content = message.content.trim();
  // If it matches file-only patterns, return empty
  if (isFileOnlyMessage(message)) return '';
  return content;
}

interface ChatMessagesProps {
  messages: Message[];
  isProcessing?: boolean;
  isTyping?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onAskAboutSelection?: (selectedText: string) => void;
  chatId?: string;
  chatTitle?: string;
  onRegenerate?: () => void;
  mode?: 'funcionarios' | 'oposiciones' | 'oposiciones_c1' | 'oposiciones_guardia_civil' | 'oposiciones_policia_nacional' | 'oposiciones_iipp' | 'oposiciones_c2' | 'oposiciones_generico';
  onAnalyzeChronology?: (fileName: string, extractedText: string) => void;
  selectedResolutions: any[];
  onToggleSelect: (resolution: any) => void;
}

export function ChatMessages({ 
  messages, 
  isProcessing, 
  isTyping,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onAskAboutSelection,
  chatId,
  chatTitle,
  onRegenerate,
  mode,
  onAnalyzeChronology,
  selectedResolutions,
  onToggleSelect
}: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  const scrollToBottom = () => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing, isTyping]);

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShouldAutoScroll(isNearBottom);

    if (scrollTop === 0 && hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  };

  const isOposiciones = mode?.startsWith('oposiciones');

  if (messages.length === 0 && !isProcessing && !isTyping) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="text-center max-w-xl space-y-4">
          <h1 className={`text-2xl font-semibold ${isOposiciones ? 'bg-gradient-to-r from-violet-700 via-purple-600 to-violet-700 bg-clip-text text-transparent' : 'text-[#002B5B]'}`}>
            {isOposiciones
              ? '¿Qué quieres estudiar hoy?'
              : 'Empecemos, ¿qué consulta jurídica necesitas revisar?'}
          </h1>
          <p className="text-base text-[#6B7280]">
            {isOposiciones
              ? mode === 'oposiciones_c1'
                ? 'Tu asistente para preparar C1 - Administración General del Estado'
                : mode === 'oposiciones_policia_nacional'
                ? 'Tu asistente para la Escala Básica de la Policía Nacional'
                : mode === 'oposiciones_guardia_civil'
                ? 'Tu asistente para la Escala de Cabos y Guardias de la Guardia Civil'
                : mode === 'oposiciones_iipp'
                ? 'Tu asistente para Ayudante de Instituciones Penitenciarias'
                : 'Tu asistente inteligente para preparar oposiciones de administración local'
              : 'Estoy aquí para ayudarte con tus expedientes, dictámenes y normativa municipal.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={messagesContainerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 bg-white relative"
    >
      {/* Text Selection Tooltip */}
      {onAskAboutSelection && (
        <TextSelectionTooltip 
          containerRef={messagesContainerRef} 
          onAskAboutSelection={onAskAboutSelection} 
        />
      )}
      {/* Load more indicator */}
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-[#002B5B]/40" />
        </div>
      )}

      {messages.map((message, index) => (
        <div
          key={message.id || `msg-${index}`}
          className={`flex ${
            message.role === 'user' ? 'justify-end' : 'justify-start'
          } animate-fade-in`}
          style={{ animationDelay: `${index * 50}ms` }}
        >
          {message.role === 'assistant' ? (
            // IA Message
            <div className="flex gap-3 max-w-[90%] sm:max-w-[85%] md:max-w-[80%]">
              <div className="flex-shrink-0">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white border-2 border-[#06D3AA] flex items-center justify-center shadow-sm overflow-hidden p-0.5">
                  {mode === 'oposiciones_policia_nacional' ? (
                    <img src={badgePN} alt="" className="w-full h-full object-contain" />
                  ) : mode === 'oposiciones_guardia_civil' ? (
                    <img src={badgeGC} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-xs font-semibold text-[#002B5B]">M</span>
                  )}
                </div>
              </div>

              <div className="flex-1">
                <LegalMarkdown 
                  content={message.content}
                  className="my-3 text-[#002B5B] leading-[1.85] tracking-wide text-sm md:text-[15px] prose prose-sm max-w-none
                    prose-p:my-3.5 prose-p:leading-[1.85] prose-p:text-[#002B5B]
                    prose-strong:text-[#002B5B] prose-strong:font-semibold
                    prose-em:text-[#002B5B] prose-em:italic
                    prose-ul:my-3 prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-1.5
                    prose-ol:my-3 prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-1.5
                    prose-li:my-1 prose-li:text-[#002B5B] prose-li:leading-[1.75]
                    prose-h1:text-[#002B5B] prose-h1:font-bold prose-h1:text-lg prose-h1:mt-6 prose-h1:mb-3
                    prose-h2:text-[#002B5B] prose-h2:font-bold prose-h2:text-base prose-h2:mt-5 prose-h2:mb-2.5
                    prose-h3:text-[#002B5B] prose-h3:font-semibold prose-h3:text-sm prose-h3:mt-4 prose-h3:mb-2
                    prose-code:text-[#002B5B] prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                    prose-blockquote:border-l-4 prose-blockquote:border-[#06D3AA] prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-4
                    prose-table:my-4 prose-td:py-1.5 prose-td:px-2 prose-th:py-1.5 prose-th:px-2
                    [&_hr]:hidden"
                />
                
                <DocumentDownload content={message.content} />
                
                {(message.metadata as any)?.legalResults && (
                  <LegalSearchResults
                    results={(message.metadata as any).legalResults}
                    selectedResolutions={selectedResolutions}
                    onToggleSelect={onToggleSelect}
                  />
                )}
                
                {message.metadata && (message.metadata.sources || message.metadata.chunks) && (
                  <SourcesCard 
                    sources={message.metadata.sources} 
                    chunks={message.metadata.chunks} 
                  />
                )}
                
                <div className="flex items-center gap-3">
                  <div className="text-xs text-[#002B5B]/40">
                    {(() => {
                      try {
                        const d = new Date(message.created_at);
                        return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                      } catch {
                        return '';
                      }
                    })()}
                  </div>
                  <MessageActions 
                    content={message.content} 
                    messageId={message.id}
                    chatId={chatId || ''}
                    isLastAssistantMessage={
                      message.role === 'assistant' &&
                      index === messages.map((m, i) => m.role === 'assistant' ? i : -1).filter(i => i !== -1).pop()
                    }
                    onRegenerate={onRegenerate}
                    mode={mode}
                  />
                </div>

                {/* Gamma auto-suggest banner for all oposiciones modes */}
                {['oposiciones','oposiciones_c1','oposiciones_c2','oposiciones_iipp','oposiciones_guardia_civil','oposiciones_policia_nacional','oposiciones_generico'].includes(mode as string) && (() => {
                  // Find the user message immediately before this assistant message
                  const prevUserMsg = index > 0 ? messages.slice(0, index).reverse().find(m => m.role === 'user') : null;
                  if (!prevUserMsg) return null;
                  const gammaFormat = detectGammaIntent(prevUserMsg.content);
                  if (!gammaFormat) return null;
                  // Extract the clean user question text
                  const userQuestion = getUserTextContent(prevUserMsg) || prevUserMsg.content.slice(0, 200);
                  return (
                    <GammaAutoSuggest
                      content={message.content}
                      detectedFormat={gammaFormat}
                      userQuestion={userQuestion}
                      chatTitle={chatTitle}
                    />
                  );
                })()}
              </div>
            </div>
          ) : (
            // User Message - show only text content (not extracted file text) + file cards
            <div className="flex gap-3 justify-end max-w-[90%] sm:max-w-[85%] md:max-w-[80%] ml-auto">
              <div className="flex-1 text-right">
                <div className="my-3">
                  {/* Only show user-typed text, never extracted file content */}
                  {(() => {
                    const userText = getUserTextContent(message);
                    return userText ? (
                      <p className="text-[#002B5B] leading-relaxed tracking-wide text-sm md:text-[15px] whitespace-pre-wrap">
                        {userText}
                      </p>
                    ) : null;
                  })()}
                  {/* Always show file cards if files are attached */}
                  {message.metadata?.files && Array.isArray(message.metadata.files) && message.metadata.files.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-end mt-2">
                      {message.metadata.files.map((file: any, idx: number) => (
                        <FilePreview
                          key={idx}
                          name={file.name}
                          size={file.size}
                          type={file.type}
                          onAnalyzeChronology={
                            (!isOposiciones && onAnalyzeChronology && file.extractedText)
                              ? () => onAnalyzeChronology(file.name, file.extractedText)
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[#002B5B]/40">
                  {(() => {
                    try {
                      const d = new Date(message.created_at);
                      return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                    } catch {
                      return '';
                    }
                  })()}
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#002B5B] flex items-center justify-center shadow-sm">
                  <span className="text-xs font-semibold text-white">U</span>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Typing Indicator */}
      {isTyping && (
        <div className="flex justify-start animate-fade-in">
          <div className="flex gap-3 max-w-[90%] sm:max-w-[85%] md:max-w-[80%]">
            <div className="flex-shrink-0">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white border-2 border-[#06D3AA] flex items-center justify-center shadow-sm overflow-hidden p-0.5">
                {mode === 'oposiciones_policia_nacional' ? (
                  <img src={badgePN} alt="" className="w-full h-full object-contain" />
                ) : mode === 'oposiciones_guardia_civil' ? (
                  <img src={badgeGC} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-xs font-semibold text-[#002B5B]">M</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-[#002B5B]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[#002B5B]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[#002B5B]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-[#002B5B]/60 tracking-wide">
                Munilex está redactando una respuesta…
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Processing State (legacy) */}
      {isProcessing && !isTyping && (
        <div className="flex justify-start animate-fade-in">
          <div className="flex gap-3 max-w-[90%] sm:max-w-[85%] md:max-w-[80%]">
            <div className="flex-shrink-0">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white border-2 border-[#06D3AA] flex items-center justify-center shadow-sm overflow-hidden p-0.5">
                {mode === 'oposiciones_policia_nacional' ? (
                  <img src={badgePN} alt="" className="w-full h-full object-contain" />
                ) : mode === 'oposiciones_guardia_civil' ? (
                  <img src={badgeGC} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-xs font-semibold text-[#002B5B]">M</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-[#002B5B]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[#002B5B]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[#002B5B]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-[#002B5B]/60 tracking-wide">
                Procesando solicitud jurídica...
              </span>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
